import React, { useRef, useEffect, useState } from 'react';
import { Play, RotateCcw, Volume2, VolumeX, Trophy } from 'lucide-react';
import { getHighScore, trySetHighScore, GAME_KEYS } from '../utils/HighScoreManager';

const SynapseSurfer = () => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [gameState, setGameState] = useState('START'); // START, PLAYING, GAME_OVER
    const gameStateRef = useRef('START'); // Ref for synchronous access in game loop
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [isNewHighScore, setIsNewHighScore] = useState(false);
    const [isMuted, setIsMuted] = useState(false); // Default to sound on (if enabled by user)
    const [soundEnabled, setSoundEnabled] = useState(true); // User preference from start screen

    // Game Constants
    // Game Constants
    // Game Constants
    const GRAVITY = 0.45;
    const THRUST = -5; // Further reduced for smoother control
    const OBSTACLE_SPEED_BASE = 4; // Slower obstacles
    const OBSTACLE_SPAWN_RATE = 2000; // Slower spawn rate (ms)
    const PLAYER_X = 100; // Fixed horizontal position
    const MAX_VELOCITY = 12; // Terminal velocity cap

    // Game Refs (Mutable state for game loop)
    const playerRef = useRef({
        y: 300,
        velocity: 0,
        radius: 20,
        isThrusting: false
    });

    const obstaclesRef = useRef([]);
    const lastTimeRef = useRef(0);
    const spawnTimerRef = useRef(0);
    const scoreRef = useRef(0);
    const speedMultiplierRef = useRef(1);
    const requestRef = useRef(null);

    // Audio Refs
    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const thrustOscillatorRef = useRef(null);

    const [countdown, setCountdown] = useState(3);
    const countdownRef = useRef(3);

    // Audio System
    const initAudio = () => {
        if (!soundEnabled || audioContextRef.current) return;

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContext();
        const gainNode = ctx.createGain();
        gainNode.connect(ctx.destination);
        gainNode.gain.value = 0.1; // Master volume

        audioContextRef.current = ctx;
        gainNodeRef.current = gainNode;
    };

    const playThrustSound = () => {
        if (isMuted || !audioContextRef.current) return;
        if (thrustOscillatorRef.current) return; // Already playing

        const ctx = audioContextRef.current;
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(100, ctx.currentTime); // Low buzz

        // Low pass filter to make it less harsh
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 400;

        osc.connect(filter);
        filter.connect(gainNodeRef.current);
        osc.start();
        thrustOscillatorRef.current = osc;
    };

    const stopThrustSound = () => {
        if (thrustOscillatorRef.current) {
            thrustOscillatorRef.current.stop();
            thrustOscillatorRef.current.disconnect();
            thrustOscillatorRef.current = null;
        }
    };

    const playScoreSound = () => {
        if (isMuted || !audioContextRef.current) return;
        const ctx = audioContextRef.current;
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

        osc.connect(gain);
        gain.connect(ctx.destination); // Connect directly or to master gain
        osc.start();
        osc.stop(ctx.currentTime + 0.1);
    };

    const playCrashSound = () => {
        if (isMuted || !audioContextRef.current) return;
        const ctx = audioContextRef.current;
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.setValueAtTime(150, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.5);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.5);
    };

    // Input Handling
    const handleStartThrust = (e) => {
        // e.preventDefault() is now handled by the non-passive listener in useEffect
        if (gameStateRef.current === 'PLAYING' || gameStateRef.current === 'COUNTDOWN') {
            playerRef.current.isThrusting = true;
            playThrustSound();
        }
    };

    const handleEndThrust = (e) => {
        // e.preventDefault() is now handled by the non-passive listener in useEffect
        playerRef.current.isThrusting = false;
        stopThrustSound();
    };

    // Non-passive event listeners for touch to prevent scrolling
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onTouchStart = (e) => {
            // Allow button clicks to pass through
            if (e.target.closest('button')) return;

            e.preventDefault(); // Prevent default touch behavior (like scrolling)
            handleStartThrust(e);
        };
        const onTouchEnd = (e) => {
            if (e.target.closest('button')) return;

            e.preventDefault(); // Prevent default touch behavior
            handleEndThrust(e);
        };

        container.addEventListener('touchstart', onTouchStart, { passive: false });
        container.addEventListener('touchend', onTouchEnd, { passive: false });

        return () => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchend', onTouchEnd);
        };
    }, [gameState]); // Re-bind if needed, or just once. handleStartThrust uses refs so it's stable-ish but depends on gameState for check.

    // Load High Score on Mount
    useEffect(() => {
        setHighScore(getHighScore(GAME_KEYS.SYNAPSE));
    }, []);

    const startGame = () => {
        initAudio(); // Initialize audio context on user interaction
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }

        setGameState('COUNTDOWN');
        gameStateRef.current = 'COUNTDOWN';
        setCountdown(3);
        countdownRef.current = 3;

        setScore(0);
        scoreRef.current = 0;
        speedMultiplierRef.current = 1;
        playerRef.current = { y: canvasRef.current.height / 2, velocity: 0, radius: 20, isThrusting: false };
        obstaclesRef.current = [];
        lastTimeRef.current = performance.now();
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        requestRef.current = requestAnimationFrame(gameLoop);
    };

    const gameOver = () => {
        stopThrustSound();
        playCrashSound();
        setGameState('GAME_OVER');
        gameStateRef.current = 'GAME_OVER';
        cancelAnimationFrame(requestRef.current);

        const finalScore = Math.floor(scoreRef.current);
        const isNew = trySetHighScore(GAME_KEYS.SYNAPSE, finalScore);
        setIsNewHighScore(isNew);
        if (isNew) {
            setHighScore(finalScore);
        } else {
            // Ensure we show the latest high score even if we didn't beat it (though it should be loaded)
            setHighScore(getHighScore(GAME_KEYS.SYNAPSE));
        }
    };

    const gameLoop = (time) => {
        if (gameStateRef.current !== 'PLAYING' && gameStateRef.current !== 'COUNTDOWN') return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const deltaTime = time - lastTimeRef.current;
        lastTimeRef.current = time;

        // Update Logic
        update(deltaTime, canvas);

        // Draw Logic
        draw(ctx, canvas);

        requestRef.current = requestAnimationFrame(gameLoop);
    };

    const update = (deltaTime, canvas) => {
        // Handle Countdown
        if (gameStateRef.current === 'COUNTDOWN') {
            countdownRef.current -= deltaTime / 1000;
            if (countdownRef.current <= 0) {
                gameStateRef.current = 'PLAYING';
                setGameState('PLAYING');
            } else {
                setCountdown(Math.ceil(countdownRef.current));
                return; // Skip physics during countdown
            }
        }

        const player = playerRef.current;

        // 1. Player Physics
        if (player.isThrusting) {
            player.velocity += THRUST * 0.15; // Apply thrust gradually
        }
        player.velocity += GRAVITY;

        // Cap Velocity (Terminal Velocity)
        if (player.velocity > MAX_VELOCITY) player.velocity = MAX_VELOCITY;
        if (player.velocity < -MAX_VELOCITY) player.velocity = -MAX_VELOCITY;

        player.y += player.velocity;

        // Ceiling/Floor Collision
        if (player.y - player.radius < 0 || player.y + player.radius > canvas.height) {
            gameOver();
            return;
        }

        // 2. Obstacles
        spawnTimerRef.current += deltaTime;
        if (spawnTimerRef.current > OBSTACLE_SPAWN_RATE / speedMultiplierRef.current) {
            spawnObstacle(canvas);
            spawnTimerRef.current = 0;
        }

        obstaclesRef.current.forEach(obs => {
            obs.x -= OBSTACLE_SPEED_BASE * speedMultiplierRef.current;
        });

        // Remove off-screen obstacles
        obstaclesRef.current = obstaclesRef.current.filter(obs => obs.x + obs.width > 0);

        // 3. Collision Detection
        // Hitbox is slightly smaller than visual radius for forgiveness
        const hitRadius = player.radius * 0.8;
        for (const obs of obstaclesRef.current) {
            // AABB Collision (Circle vs Rect simplified to Rect vs Rect for performance/simplicity in this style)
            // Or precise Circle vs Rect
            const circleDistanceX = Math.abs(PLAYER_X - (obs.x + obs.width / 2));
            const circleDistanceY = Math.abs(player.y - (obs.y + obs.height / 2));

            if (circleDistanceX > (obs.width / 2 + hitRadius)) continue;
            if (circleDistanceY > (obs.height / 2 + hitRadius)) continue;

            if (circleDistanceX <= (obs.width / 2)) { gameOver(); return; }
            if (circleDistanceY <= (obs.height / 2)) { gameOver(); return; }

            const cornerDistance_sq = (circleDistanceX - obs.width / 2) ** 2 +
                (circleDistanceY - obs.height / 2) ** 2;

            if (cornerDistance_sq <= (hitRadius ** 2)) {
                gameOver();
                return;
            }
        }

        // 4. Score & Difficulty
        const prevScore = Math.floor(scoreRef.current);
        scoreRef.current += deltaTime * 0.01;
        const newScore = Math.floor(scoreRef.current);

        if (newScore > prevScore && newScore % 10 === 0) { // Play sound every 10 points
            playScoreSound();
        }

        setScore(newScore);
        speedMultiplierRef.current = 1 + (scoreRef.current / 1000); // Speed up over time
    };

    const spawnObstacle = (canvas) => {
        const height = Math.random() * (canvas.height * 0.6) + 50; // Random height
        const isTop = Math.random() > 0.5;

        obstaclesRef.current.push({
            x: canvas.width,
            y: isTop ? 0 : canvas.height - height,
            width: 60,
            height: height,
            color: '#f472b6' // Neon Pink
        });
    };

    const draw = (ctx, canvas) => {
        // Clear Background
        ctx.fillStyle = '#0f172a'; // Dark Blue/Black
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw Grid/Wireframe Background (Tron effect)
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i < canvas.width; i += 50) {
            ctx.moveTo(i - (scoreRef.current * 2) % 50, 0); // Moving grid effect
            ctx.lineTo(i - (scoreRef.current * 2) % 50, canvas.height);
        }
        for (let i = 0; i < canvas.height; i += 50) {
            ctx.moveTo(0, i);
            ctx.lineTo(canvas.width, i);
        }
        ctx.stroke();

        // Draw Player (Cyan Orb)
        const player = playerRef.current;
        ctx.shadowBlur = 20;
        ctx.shadowColor = '#22d3ee';
        ctx.fillStyle = '#22d3ee';
        ctx.beginPath();
        ctx.arc(PLAYER_X, player.y, player.radius, 0, Math.PI * 2);
        ctx.fill();

        // Draw Tail (Speed effect)
        ctx.beginPath();
        ctx.moveTo(PLAYER_X - player.radius, player.y);
        ctx.lineTo(PLAYER_X - player.radius - 40 - (player.velocity * 2), player.y);
        ctx.strokeStyle = 'rgba(34, 211, 238, 0.5)';
        ctx.lineWidth = player.radius;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.shadowBlur = 0; // Reset shadow

        // Draw Obstacles (Pink Blocks)
        ctx.fillStyle = '#f472b6';
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#f472b6';
        obstaclesRef.current.forEach(obs => {
            ctx.fillRect(obs.x, obs.y, obs.width, obs.height);

            // Inner detail
            ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.fillRect(obs.x + 5, obs.y + 5, obs.width - 10, obs.height - 10);
            ctx.fillStyle = '#f472b6'; // Reset
        });
        ctx.shadowBlur = 0;

        // Draw Velocity/Power Meter
        const meterX = 20;
        const meterY = canvas.height / 2;
        const meterHeight = 200;
        const meterWidth = 10;

        // Meter Background
        ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
        ctx.fillRect(meterX, meterY - meterHeight / 2, meterWidth, meterHeight);
        ctx.strokeStyle = '#334155';
        ctx.strokeRect(meterX, meterY - meterHeight / 2, meterWidth, meterHeight);

        // Center Line (Zero Velocity)
        ctx.beginPath();
        ctx.moveTo(meterX - 5, meterY);
        ctx.lineTo(meterX + meterWidth + 5, meterY);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.stroke();

        // Velocity Bar
        // Velocity is roughly -10 (up) to +10 (down)
        const velocity = player.velocity;
        const barHeight = (velocity / 15) * (meterHeight / 2); // Scale velocity to bar height

        ctx.fillStyle = velocity < 0 ? '#22d3ee' : '#f472b6'; // Cyan for up, Pink for down
        ctx.fillRect(meterX, meterY, meterWidth, barHeight);

        // Thrust Indicator (Glow)
        if (player.isThrusting) {
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#22d3ee';
            ctx.fillStyle = '#22d3ee';
            ctx.fillRect(meterX - 2, meterY - meterHeight / 2 - 10, meterWidth + 4, 4);
            ctx.shadowBlur = 0;
        }

        // Draw Countdown
        if (gameStateRef.current === 'COUNTDOWN') {
            ctx.fillStyle = 'rgba(15, 23, 42, 0.5)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.font = 'bold 120px monospace';
            ctx.fillStyle = '#22d3ee';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.shadowBlur = 30;
            ctx.shadowColor = '#22d3ee';
            ctx.fillText(Math.ceil(countdownRef.current), canvas.width / 2, canvas.height / 2);
            ctx.shadowBlur = 0;
        }
    };

    // Resize Handler
    useEffect(() => {
        const handleResize = () => {
            if (canvasRef.current && containerRef.current) {
                canvasRef.current.width = containerRef.current.clientWidth;
                canvasRef.current.height = containerRef.current.clientHeight;
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial sizing

        // Slight delay to ensure container is rendered and sized correctly on mobile
        setTimeout(handleResize, 100);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden bg-slate-900 select-none touch-none"
            onMouseDown={handleStartThrust}
            onMouseUp={handleEndThrust}
            onMouseLeave={handleEndThrust}
        >
            <canvas ref={canvasRef} className="block" />

            {/* HUD */}
            <div className="absolute top-4 right-6 text-right pointer-events-none">
                <div className="text-cyan-400 font-mono text-3xl font-bold tracking-wider drop-shadow-lg">
                    {score}
                </div>
                <div className="text-slate-500 text-xs font-mono uppercase tracking-widest">
                    Score
                </div>
            </div>

            {/* Mute Toggle */}
            <button
                onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setIsMuted(!isMuted); }}
                className="absolute top-4 left-4 p-2 text-cyan-500/50 hover:text-cyan-400 transition-colors z-20"
            >
                {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            </button>

            {/* Start Screen */}
            {gameState === 'START' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm z-50">
                    <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-8 tracking-tighter drop-shadow-2xl text-center px-4">
                        SYNAPSE SURFER
                    </h1>
                    <button
                        onClick={(e) => { e.stopPropagation(); startGame(); }}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); startGame(); }}
                        className="group relative px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-xl rounded-full transition-all transform hover:scale-110 shadow-[0_0_20px_rgba(34,211,238,0.6)] z-50 pointer-events-auto"
                    >
                        <div className="flex items-center gap-2 pointer-events-none">
                            <Play fill="currentColor" />
                            <span>INITIATE LINK</span>
                        </div>
                    </button>
                    <div className="mt-6 flex flex-col items-center gap-2">
                        <div className="text-cyan-400 font-mono text-lg flex items-center gap-2">
                            <Trophy size={20} />
                            <span>BEST: {highScore}</span>
                        </div>
                        <p className="text-cyan-200/60 font-mono text-sm animate-pulse text-center px-4">
                            Tap and hold to surf the neural pathway
                        </p>
                    </div>

                    {/* Sound Toggle on Start Screen */}
                    <div className="mt-4 flex items-center gap-2 z-50">
                        <input
                            type="checkbox"
                            id="soundToggle"
                            checked={soundEnabled}
                            onChange={(e) => setSoundEnabled(e.target.checked)}
                            className="accent-cyan-500 w-4 h-4 cursor-pointer"
                        />
                        <label htmlFor="soundToggle" className="text-cyan-500/80 font-mono text-xs cursor-pointer select-none">
                            ENABLE SOUND
                        </label>
                    </div>
                </div>
            )}
            {/* Game Over Screen */}
            {gameState === 'GAME_OVER' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-md z-10">
                    <h2 className="text-4xl font-bold text-pink-500 mb-2 drop-shadow-[0_0_10px_rgba(244,114,182,0.8)]">
                        CONNECTION LOST
                    </h2>
                    <div className="text-6xl font-mono text-white mb-4">
                        {score}
                    </div>

                    {isNewHighScore ? (
                        <div className="mb-8 flex flex-col items-center animate-bounce">
                            <span className="text-yellow-400 font-bold text-xl tracking-widest flex items-center gap-2">
                                <Trophy size={24} /> NEW HIGH SCORE!
                            </span>
                        </div>
                    ) : (
                        <div className="text-slate-400 mb-8 font-mono flex items-center gap-4">
                            <span>Your Score: <span className="text-white">{score}</span></span>
                            <span className="text-slate-600">|</span>
                            <span>Best: <span className="text-cyan-400">{highScore}</span></span>
                        </div>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); startGame(); }}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); startGame(); }}
                        className="px-8 py-3 bg-transparent border-2 border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-slate-900 font-bold rounded-full transition-all flex items-center gap-2"
                    >
                        <RotateCcw size={20} />
                        <span>RECONNECT</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SynapseSurfer;
